import React, { useState, useEffect } from "react";
import { NavLink, Navigate, useLocation, Link } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { userService } from "../../_services";
import SignupHeader from "../Register/signup_header";
import Logo from "../../images/logo.png";
import Signupleft from "../../images/signupleft.webp";

const formSchema = z.object({
  username: z.string().min(1, { message: "Please enter a valid username" }),
  password: z.string().min(1, { message: "Please enter your password" }),
  remember: z.boolean().optional(),
});

const LogIn = () => {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            username: '',
            password: '',
            remember: false,
        },
    });

    // Initialize with remembered credentials
    useEffect(() => {
        try {
            const cookies = new Cookies();
            const CryptoJS = require("crypto-js");
            const encryptedUsername = cookies.get('_col_uuid');
            const encryptedPassword = cookies.get('__gads');

            if (encryptedUsername && encryptedPassword) {
                const username = CryptoJS.AES.decrypt(encryptedUsername, 'sblw3hn8').toString(CryptoJS.enc.Utf8);
                const password = CryptoJS.AES.decrypt(encryptedPassword, 'sblw3hn8').toString(CryptoJS.enc.Utf8);
                form.setValue('username', username);
                form.setValue('password', password);
                form.setValue('remember', true);
            }
        } catch (error) {
            console.error('Error loading saved credentials:', error);
        }
    }, [form]);

    // Handle third-party token authentication
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        
        if (code) {
            handleTokenAuth(code);
        }
    }, [location.search]);

    const handleTokenAuth = async (code) => {
        try {
            setIsLoading(true);
            const tokenData = await userService.getToken(code, 'RxHckerPro');
            
            if (tokenData.length > 0) {
                const userInfoData = await userService.getTokenBased_UserInfo(tokenData[0].access_token, 'RxHckerPro');
                
                if (userInfoData.length > 0) {
                    setIsLoggedIn(true);
                    handlePostLoginRedirect();
                } else {
                    form.setError('password', { message: 'Username or password is incorrect' });
                }
            } else {
                form.setError('password', { message: 'Username or password is incorrect' });
            }
        } catch (error) {
            form.setError('password', { message: 'Authentication failed. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    const saveCredentialsToCookies = (username, password) => {
        try {
            const CryptoJS = require("crypto-js");
            const cookies = new Cookies();
            cookies.set('_col_uuid', CryptoJS.AES.encrypt(username, 'sblw3hn8').toString(), { path: '/' });
            cookies.set('__gads', CryptoJS.AES.encrypt(password, 'sblw3hn8').toString(), { path: '/' });
        } catch (error) {
            console.error('Error saving credentials:', error);
        }
    };

    const handlePostLoginRedirect = () => {
        try {
            // Handle popup window for third-party integration
            if (window.opener && window.self.name === "popup") {
                const currentUrl = window.location.href;
                const wparam = localStorage.getItem('wt19:awaydpset');
                
                if (currentUrl.toLowerCase().includes("release-backoffice") || currentUrl.toLowerCase().includes("dev-backoffice") || currentUrl.toLowerCase().includes("shadcn-backoffice")) {
                    window.opener.location.href = 'http://dev.glichealth.com/login?q=' + wparam;
                } else if (currentUrl.toLowerCase().includes("localhost:")) {
                    window.opener.location.href = 'http://localhost:5464/login?q=' + wparam;
                } else {
                    window.opener.location.href = 'https://reporting.nulifespanrx.com/login?q=' + wparam;
                }
                window.self.close();
            }
        } catch (error) {
            console.error('Post-login redirect error:', error);
        }
    };

    const onSubmit = async (values) => {
        setIsLoading(true);
        
        try {
            const apiData = await userService.login(values.username, values.password);

            if (apiData.length > 0) {
                if (apiData[0].code !== "-1") {
                    setIsLoggedIn(true);
                    
                    // Save credentials if remember me is checked
                    if (values.remember) {
                        saveCredentialsToCookies(values.username, values.password);
                    }
                    
                    handlePostLoginRedirect();
                } else {
                    form.setError('password', { message: apiData[0].message });
                    form.setValue('password', '');
                }
            } else {
                form.setError('password', { message: 'Login failed. Please try again.' });
            }
        } catch (error) {
            form.setError('password', { message: 'An error occurred. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Check if user is already logged in
    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        const userType = window.atob(localStorage.getItem('_Host_Pro_type'));
        
        if (userType !== "Admin") {
            if (userType === "consumer") {
                return <Navigate replace to="/consumer/summary" />;
            }
            return <Navigate replace to="/summary" />;
        }
        return <Navigate replace to="/Admin/groups" />;
    }

    if (isLoggedIn) {
        const userType = window.atob(localStorage.getItem('_Host_Pro_type'));
        
        if (userType !== "Admin") {
            if (userType === "consumer") {
                return <Navigate replace to="/consumer/summary" />;
            }
            return <Navigate replace to="/summary" />;
        }
        return <Navigate replace to="/Admin/groups" />;
    }

    return (
        <div className="min-h-screen bg-secondary">
            <SignupHeader />
            
            <div className="w-full mx-auto">
                <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
                    <div className="w-full max-w-[1260px] mx-auto px-4 md:px-8">
                        <div className="flex flex-col md:flex-row items-stretch gap-8 md:gap-16">
                            {/* Left side - Image */}
                            <div className="flex-1 flex items-center justify-center md:max-w-[689px]">
                                <img 
                                    src={Signupleft} 
                                    alt="Welcome to NuLifeSpan Rx" 
                                    className="w-full h-auto object-contain"
                                />
                            </div>

                            {/* Right side - Login Form */}
                            <div className="flex-1 flex items-center justify-center md:max-w-[490px]">
                                <div className="w-full space-y-6">
                                    <div className="space-y-2 text-center md:text-left">
                                        <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                                            Welcome back
                                        </h1>
                                        <p className="text-md text-foreground opacity-70">
                                            Please sign in to continue
                                        </p>
                                    </div>

                                    <Form {...form}>
                                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                            <FormField
                                                control={form.control}
                                                name="username"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <div className="relative">
                                                                <Input 
                                                                    {...field} 
                                                                    disabled={isLoading}
                                                                    placeholder=" "
                                                                    className="peer h-12"
                                                                />
                                                                <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                                                    Username
                                                                </FormLabel>
                                                            </div>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="password"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <div className="relative">
                                                                <Input 
                                                                    {...field} 
                                                                    type={showPassword ? "text" : "password"}
                                                                    disabled={isLoading}
                                                                    placeholder=" "
                                                                    className="peer pr-10 h-12"
                                                                />
                                                                <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                                                    Password
                                                                </FormLabel>
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                                                >
                                                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                                </Button>
                                                            </div>
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <div className="flex items-center justify-between py-2">
                                                <FormField
                                                    control={form.control}
                                                    name="remember"
                                                    render={({ field }) => (
                                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value}
                                                                    onCheckedChange={field.onChange}
                                                                    disabled={isLoading}
                                                                />
                                                            </FormControl>
                                                            <div className="space-y-1 leading-none">
                                                                <FormLabel className="text-sm font-normal text-foreground">
                                                                    Remember me
                                                                </FormLabel>
                                                            </div>
                                                        </FormItem>
                                                    )}
                                                />
                                                <Link to="/ForgotPassword" className="text-sm text-accent hover:text-accent/90">
                                                    Forgot Password?
                                                </Link>
                                            </div>

                                            <Button type="submit" variant="secondary" size="lg" className="w-auto" disabled={isLoading}>
                                                {isLoading ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Signing in...
                                                    </>
                                                ) : (
                                                    'Sign In'
                                                )}
                                            </Button>
                                        </form>
                                    </Form>

                                    <div className="mt-6 text-center md:text-left space-y-2">
                                        <p className="text-md text-foreground opacity-70">
                                            Don't have an account?{' '}
                                            <Link to="/signup" className="text-accent hover:no-underline hover:text-foreground font-medium">
                                                Sign Up here
                                            </Link>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogIn;
